/* eslint-disable brace-style, no-param-reassign, yoda */
import Quizzes from 'es6/controllers/apps/quizzes';
import SequencerBase from 'es6/controllers/apps/sequencer-base';
import { ResultTypes } from 'es6/models/quiz-result';

const EXPECTED_CORRECT_RATIO = 0.8;

const MAX_INSERTIONS = 2;
const MAX_PRESENTATIONS = 5;

export default class IKnowSequencer extends SequencerBase {
  constructor(sessionData, quizTimer) {
    super(sessionData.studySession, false, quizTimer);
    this.currentPresentationIndex = 0;
  }

  showNextQuiz() {
    const itemsWithQuizzesRemaining = this._itemsWithQuizzesRemaining();
    const previousQuizId = this.activeQuiz ? this.activeQuiz.itemPackage.getId() : null;
    let nextItem;
    const self = this;

    if (itemsWithQuizzesRemaining.length === 0) {
      this.sequenceFinished();
      return;
    }

    // If only one item remains and the previous presented quiz was testing that item,
    // insert a spacer quiz for a completed item.
    if (itemsWithQuizzesRemaining.length === 1 &&
      itemsWithQuizzesRemaining[0].content.getId() === previousQuizId)
    {
      // Select a substitute item to have a spacer quiz inserted for.
      nextItem = _.chain(this._studyItems)
        .reject(function (item) {
          // exclude the quiz we are trying to space out and any skipped items.
          return item.content.getId() === previousQuizId || item.skipped;
        })
        .shuffle()
        .min(function (item) {
          // The combination of shuffle and min will randomly select from amongst the
          // items with lowest-equal score calculated as follows.

          // The more quizzes already presented for an item the less desirable it is.
          let score = item.sequencing.presentedQuizzes.length;

          // But overriding this we want to avoid items that don't have possible quizzes
          // not already been presented in this session.
          const presentedQuizzes = _.pluck(item.sequencing.presentedQuizzes, 'quizType');
          const unpresentedQuizzes = _.difference(item.sequencing.possibleQuizTypes, presentedQuizzes);
          if (_.isEmpty(unpresentedQuizzes)) {
            score += 100; // Any large number would do here.
          }

          // And also want to avoid items that the user has marked as easy in this session.
          const markedAsEasy = _.some(item.sequencing.presentedQuizzes, function (quiz) {
            return quiz.result.leQuizResult === ResultTypes.EASY;
          });
          if (markedAsEasy) {
            score += 50;
          }

          // But preferably include items marked as hard.
          _.each(item.sequencing.presentedQuizzes, function (quiz) {
            if (quiz.result.leQuizResult === ResultTypes.HARD) {
              // reduce by 1, essentially cancelling the penalty of an extra quiz having been
              // shown for the item.
              score -= 1;
            }
          });

          return score;
        })
        .value();

      // If no valid spacers were found (e.g. if all skipped), do the only thing
      // possible and show another quiz for the previously quizzed item.
      if (nextItem === Infinity) { // _.min([]) gives Infinity
        this._showNextQuizForItem(this._studyItemFromQuiz(this.activeQuiz));
        return;
      }

      // Insert a quiz for the substitute item
      nextItem.sequencing.remainingQuizzes.unshift(this.selectSubstituteQuiz(nextItem));
      nextItem.sequencing.extraQuizzesInserted++;
    } else if (itemsWithQuizzesRemaining.length === 1) {
      // If only one item has quizzes left, present a quiz for that item.
      nextItem = itemsWithQuizzesRemaining[0];
    } else {
      // Assign scores to a pool of potential items then select between them by
      // roulette wheel selection.

      let totalScore = 0;

      // Find a single unseen item to add to the pool of potential items to show
      const insertableUnseenItem = _.chain(itemsWithQuizzesRemaining)
        .sortBy(function (item) {
          // Order by number of remaining quizzes so that the selected item
          // has the most remaining quizzes among unseenItems.
          return -item.sequencing.remainingQuizzes.length;
        })
        .find(function (item) {
          return item.sequencing.lastSeenAt === -1;
        })
        .value();

      const pool = _.chain(itemsWithQuizzesRemaining)
        // Filter out the item tested by the previous quiz and unstarted items
        .reject(function (item) {
          return item.content.getId() === previousQuizId || item.sequencing.lastSeenAt === -1;
        })
        // Add back in the single unseen item
        .union(insertableUnseenItem ? [insertableUnseenItem] : [])
        // Assign scores to all items in the pool.
        .each(function (item) {
          const score = self.scoreItem(item);
          item.sequencing.score = score;
          totalScore += score;
        })
        .value();

      // Select from the pool via roulette wheel selection on the items' scores.
      let targetPosition = _.random(0, totalScore - 1);
      nextItem = _.find(pool, function (item) {
        // Decrement by how far the current item moves us around the wheel.
        targetPosition -= item.sequencing.score;
        // Return whether the target was in the current section.
        return (0 > targetPosition);
      });
    }

    this._showNextQuizForItem(nextItem);
  }

  scoreItem(item) {
    if (item.sequencing.lastSeenAt === -1) {
      // The one and only unstarted item is given a fixed score. Its probability of inclusion
      // depends on this score relative to the scores assigned to started-but-not-yet-completed
      // items (Generally the more such items, the lower its chance of inclusion).
      return 32;
    } else {
      // Score determined by the number of quizzes remaining for the item (strong preference
      // for items with multiple remaining quizzes).
      const baseScore = Math.pow(3, item.sequencing.remainingQuizzes.length - 1);

      // And the interval since a quiz for the item was last presented (preference for items that
      // havent been seen in awhile).
      const lastSeenInterval = (this.currentPresentationIndex - item.sequencing.lastSeenAt);

      return baseScore * this.scoreItemIntervalMultiplier(lastSeenInterval);
    }
  }

  // Hard coded results of the interval modifier calculation.
  scoreItemIntervalMultiplier(interval) {
    const intervalMultipliers = [0.5, 1, 2, 4, 8, 16];
    if (interval < intervalMultipliers.length) {
      return intervalMultipliers[interval];
    } else {
      return _.last(intervalMultipliers);
    }
  }

  // Helper function to calculate the study interval multiplier
  // Exponential growth out to TARGET_FACTOR at the TARGET_INTERVAL.
  // Currently gives the following vales: f(2)=1, f(3)=2, f(4)=4,
  // f(5)=8, f(6)=16, f(7)=16, f(8)=16, ...
  /* TARGET_INTERVAL: 4,
  TARGET_FACTOR:   16,
  function intervalMultiplier(interval) {
    // NOTE: these are hard coded above, I'm just keeping the
    // calculation here as I may want to adjust the constants in future.
    var spacingExpBase = Math.pow(this.TARGET_FACTOR, 1/this.TARGET_INTERVAL);

    // Exponential only until the TARGET_INTERVAL; flat beyond it.
    interval = Math.min(interval-1, this.TARGET_INTERVAL);
    return Math.pow(spacingExpBase, interval);
  }*/

  quizFinished(quiz) {
    this.currentPresentationIndex++;

    const studyItem = this._studyItemFromQuiz(quiz);
    studyItem.sequencing.presentedQuizzes.push(quiz);

    const quizResult = quiz.result;
    if (quizResult.isCorrect()) {
      if (quizResult.leQuizResult === ResultTypes.HARD) {
        this._onHardAnswer(studyItem);
      } else if (quizResult.leQuizResult === ResultTypes.EASY) {
        this._onEasyAnswer(studyItem);
      }
    } else {
      this._onIncorrectAnswer(quiz, studyItem);
    }

    this._incrementProgress();
    this.showNextQuiz();
  }

  _onEasyAnswer(studyItem) {
    // If the user marks the item as easy, they probably don't want to see it again.
    studyItem.sequencing.remainingQuizzes = [];
  }

  _onHardAnswer(studyItem) {
    // Ensure there is at least one more quiz for the studyItem.
    if (studyItem.sequencing.remainingQuizzes.length === 0) {
      this._attemptInsertingExtraQuiz(studyItem);
    }
  }

  _onIncorrectAnswer(quiz, studyItem) {
    // Add a quiz to replace the one that was answered incorrectly.
    const repeatQuiz = this._getReplacementForIncorrectQuiz(quiz, studyItem);
    const remainingQuizzes = studyItem.sequencing.remainingQuizzes;
    remainingQuizzes.unshift(repeatQuiz);

    if (remainingQuizzes.length === 1) {
      // Insert an extra quiz if mistake was on the final scheduled quiz for the item.
      this._attemptInsertingExtraQuiz(studyItem);
    } else {
      // Otherwise reschedule the incorrectly answered quiz one place
      // later in the study sequence for that item.
      remainingQuizzes[0] = remainingQuizzes[1];
      remainingQuizzes[1] = repeatQuiz; // Previously at position 0
    }
  }

  _getReplacementForIncorrectQuiz(quiz, studyItem) {
    // Duplicate the incorrectly answered quiz.
    return new Quizzes[quiz.quizType](
      quiz.itemPackage,
      quiz.result.goalId,
      this._isDiagnosticQuiz,
      this.quizFinished.bind(this),
      this._quizTimer
    );
  }

  _attemptInsertingExtraQuiz(studyItem) {
    // Don't insert when there are no other items with quizzes remaining, in which
    // case the insertion would also require the insertions of too many spacers.
    if (this._itemsWithQuizzesRemaining().length === 1) {
      return;
    }

    // And don't when we have already inserted MAX_INSERTIONS quizzes for that item,
    // or it has had a total of more than MAX_PRESENTATIONS total presentations.
    // NOTE: we might want to revisit this in future, from a learning perspective,
    // its better to keep inserting (particular unique quizzes), but don't want
    // to make sessions feel unfinishable towards the end.
    if (studyItem.sequencing.extraQuizzesInserted > MAX_INSERTIONS ||
      studyItem.sequencing.presentedQuizzes.length > MAX_PRESENTATIONS)
    {
      return;
    }
    studyItem.sequencing.remainingQuizzes.unshift(this.selectSubstituteQuiz(studyItem));
    studyItem.sequencing.extraQuizzesInserted++;
  }

  selectSubstituteQuiz(studyItem) {
    const possibleQuizTypes = studyItem.sequencing.possibleQuizTypes;
    const presentedQuizTypes = _.pluck(studyItem.sequencing.presentedQuizzes, 'quizType');
    const remainingQuizTypes = _.pluck(studyItem.sequencing.remainingQuizzes, 'quizType');

    // Priority given to quizzes not already presented or scheduled in the current session.
    let insertable = _.difference(possibleQuizTypes, presentedQuizTypes, remainingQuizTypes);

    // Otherwise prefer quizzes that have already been presented to those that are scheduled.
    if (insertable.length === 0) {
      insertable = _.difference(possibleQuizTypes, remainingQuizTypes);
    }

    // If all else fails, take one of the upcoming quizzes.
    if (insertable.length === 0) {
      insertable = remainingQuizTypes;
    }

    // Sample and build quiz.
    const substituteQuiz = this._buildQuiz(studyItem, _.sample(insertable));

    return substituteQuiz;
  }

  /**
   * @Override
   * Adjustment to _expectedRemainingQuizCount due to item insertion.
   */
  _expectedRemainingQuizCount() {
    // The expected number of quizzes remaining is greater than the number of scheduled
    // quizzes remaining as the user will get some fraction of the remaining quizzes wrong
    // which will then need to be repeated. This could be made more accurate by also taking
    // extra quiz insertions into account.
    return super._expectedRemainingQuizCount() * (1 / EXPECTED_CORRECT_RATIO);
  }
}
